import type { PropsWithChildren, ReactElement } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { GetLeadTimeDatesQueryVariables } from '@buying/library';

import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
    GetLeadTimeDatesDocument,
    GraphClientNames,
    isDefined,
} from '@buying/library';
import { getISOWeek } from 'date-fns';

import { emptyLeadTimeDate } from './utils';

interface LeadTimeDate {
    cargoClosingDate: Date | null;
    iltWorstCaseProperty: string | null;
    warehouseArrival: Date | null;
    cancelDate: Date | null;
    ccDateWeekNumber: string | null;
    whaDateWeekNumber: string | null;
}

interface LeadTimeDatesContextType {
    leadTimeDates: Array<LeadTimeDate>;
    isLeadTimeDatesLoading: boolean;
}

type LeadTimeDatesProviderProps = Partial<GetLeadTimeDatesQueryVariables>;

const LeadTimeDatesContext = createContext<LeadTimeDatesContextType | null>(
    null
);

const useLeadTimeDates = (): LeadTimeDatesContextType => {
    const context = useContext(LeadTimeDatesContext);

    if (!context)
        throw new Error(
            'useLeadTimeDates must be used within a LeadTimeDatesProvider'
        );
    return context;
};

const LeadTimeDatesProvider = ({
    transportTerm,
    pointOfOriginNumber,
    destinationCountryCode,
    showPastDates = false,
    children,
}: PropsWithChildren<LeadTimeDatesProviderProps>): ReactElement => {
    const shouldSkipFetch =
        !pointOfOriginNumber || !transportTerm || !destinationCountryCode;

    const leadTimeDatesVariables: GetLeadTimeDatesQueryVariables = useMemo(
        () => ({
            destinationCountryCode: destinationCountryCode ?? '',
            pointOfOriginNumber: pointOfOriginNumber ?? 0,
            transportTerm: transportTerm ?? '',
            showPastDates,
        }),
        [
            destinationCountryCode,
            pointOfOriginNumber,
            transportTerm,
            showPastDates,
        ]
    );

    const { data, networkStatus } = useQuery(GetLeadTimeDatesDocument, {
        context: {
            clientName: GraphClientNames['bestone-bi4-buying-comm-service'],
        },
        skip: shouldSkipFetch,
        variables: leadTimeDatesVariables,
    });

    const isLoadingLeadTimeDates =
        networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.setVariables;

    const filteredLeadTimeDates = useMemo(
        () => data?.leadTimeDates?.filter(isDefined) ?? [],
        [data]
    );

    const mappedLeadTimeDates: Array<LeadTimeDate> = useMemo(() => {
        // Early return if no lead time dates are available
        if (filteredLeadTimeDates.length === 0) return [];

        // We also add an empty lead time date at the start of the array, so that we have a default option
        return [
            emptyLeadTimeDate,
            ...filteredLeadTimeDates.map((leadTimeDate) => ({
                cancelDate: new Date(leadTimeDate.cancelDate),
                cargoClosingDate: new Date(leadTimeDate.cargoClosingDate),
                ccDateWeekNumber: `W${getISOWeek(new Date(leadTimeDate.cargoClosingDate))}`,
                iltWorstCaseProperty: leadTimeDate.propertyValue,
                warehouseArrival: new Date(leadTimeDate.warehouseArrivalDate),
                whaDateWeekNumber: `W${getISOWeek(new Date(leadTimeDate.warehouseArrivalDate))}`,
            })),
        ];
    }, [filteredLeadTimeDates]);

    const leadTimeDatesContextValue = useMemo(
        () => ({
            leadTimeDates: mappedLeadTimeDates,
            isLeadTimeDatesLoading: isLoadingLeadTimeDates,
        }),
        [mappedLeadTimeDates, isLoadingLeadTimeDates]
    );

    return (
        <LeadTimeDatesContext.Provider value={leadTimeDatesContextValue}>
            {children}
        </LeadTimeDatesContext.Provider>
    );
};

export { LeadTimeDatesProvider, useLeadTimeDates };
export type { LeadTimeDate };
